import { useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";

type Value = (string | number)[];

export function TagsInput({
  value,
  onChange,
  placeholder,
  isCurrency = false,
}: {
  value?: Value | string | null;
  onChange: (next: Value) => void;
  placeholder?: string;
  isCurrency?: boolean;
}) {
  const arr: Value = Array.isArray(value)
    ? value
    : typeof value === "string" && value.trim()
      ? value
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      : [];
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const t = raw.trim().replace(/,$/, "");
    if (!t) {
      return;
    }
    const next: string | number = isNaN(Number(t)) ? t : Number(t);
    if (arr.some(x => String(x) === String(next))) {
      return;
    }
    onChange([...arr, next]);
    setDraft("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && arr.length) {
      onChange(arr.slice(0, -1));
    }
  };

  return (
    <div className="bg-muted/40 flex min-h-11 flex-wrap items-center gap-1.5 rounded-xl border border-transparent px-2.5 py-1.5 transition-colors focus-within:border-input focus-within:bg-background">
      {arr.map((tag, i) => (
        <span
          key={`tag-${String(tag)}`}
          className="inline-flex items-center gap-1 rounded-md border border-amber-100/50 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900"
        >
          {isCurrency && typeof tag === "number"
            ? `₹${tag.toLocaleString("en-IN")}`
            : tag}
          <button
            type="button"
            onClick={() => onChange(arr.filter((_, idx) => idx !== i))}
            className="rounded p-0.5 text-amber-900/60 transition-colors hover:bg-amber-100/85 hover:text-amber-900"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => draft && commit(draft)}
        placeholder={
          arr.length === 0 ? (placeholder ?? "Type and press Enter…") : ""
        }
        className="h-7 min-w-[100px] flex-1 bg-transparent px-1 text-sm outline-none"
      />
    </div>
  );
}
